import { createTemplates } from './templates.js';

const AI_ENDPOINT = 'https://wertx-ai.theanshumansrivastava12.workers.dev/chat';

const MODELS = {
  wertx: [
    { id: '3.0-core', version: '3.0', name: 'Core', tier: 'Pro' },
    { id: '2.6-sonic', version: '2.6', name: 'Sonic', tier: 'Pro' },
    { id: '1.7-flash', version: '1.7', name: 'Flash', tier: 'Go' },
    { id: '1.1-ask', version: '1.1', name: 'Ask', tier: 'Free' }
  ],
  point: [
    { id: '2.9-target', version: '2.9', name: 'Target', tier: 'Pro' },
    { id: '2.0-aim', version: '2.0', name: 'Aim', tier: 'Go' },
    { id: '2.1-light', version: '2.1', name: 'Light', tier: 'Free' }
  ]
};

const icon = (name, size = 18) => {
  const paths = {
    chat: '<path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/>',
    play: '<path d="m8 5 11 7-11 7z"/>',
    history: '<path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5M12 7v5l3 2"/>',
    settings: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.03 1.56V21h-4v-.08A1.7 1.7 0 0 0 9 19.37a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.63 15 1.7 1.7 0 0 0 3.08 14H3v-4h.08A1.7 1.7 0 0 0 4.63 9a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.63 1.7 1.7 0 0 0 10 3.08V3h4v.08A1.7 1.7 0 0 0 15 4.63a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.37 9c.16.6.8 1 1.55 1H21v4h-.08c-.75 0-1.39.4-1.52 1z"/>',
    plus: '<path d="M12 5v14M5 12h14"/>',
    search: '<circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/>',
    attach: '<path d="m21.4 11.6-8.8 8.8a6 6 0 0 1-8.5-8.5l9.2-9.2a4 4 0 0 1 5.7 5.7l-9.2 9.2A2 2 0 0 1 7 14.8l8.5-8.5"/>',
    send: '<path d="m22 2-7 20-4-9-9-4zM22 2 11 13"/>',
    code: '<path d="m8 9-3 3 3 3m8-6 3 3-3 3m-2-9-4 12"/>',
    trash: '<path d="M3 6h18M8 6V4h8v2m-9 0 1 15h8l1-15M10 10v7m4-7v7"/>',
    copy: '<rect x="9" y="9" width="12" height="12" rx="2"/><rect x="3" y="3" width="12" height="12" rx="2"/>',
    menu: '<path d="M4 7h16M4 12h16M4 17h16"/>',
    close: '<path d="m6 6 12 12M18 6 6 18"/>',
    check: '<path d="m5 12 4 4L19 6"/>',
    expand: '<path d="M8 3H3v5m13-5h5v5M8 21H3v-5m13 5h5v-5"/>'
  };
  return `<svg aria-hidden="true" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${paths[name] || ''}</svg>`;
};

const escapeHtml = (value = '') => String(value).replace(/[&<>"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
const readJSON = (key, fallback) => { try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; } };

export class App {
  constructor() {
    this.templates = createTemplates();
    this.ai = localStorage.getItem('wertx.ai') || 'wertx';
    this.models = readJSON('wertx.models', { wertx: '3.0-core', point: '2.9-target' });
    this.settings = readJSON('wertx.settings', { autoRun: true, stream: true, density: 'comfortable', theme: 'dark' });
    this.chats = readJSON('wertx.chats.v2', []);
    this.chatId = null;
    this.messages = [];
    this.files = [];
    this.view = 'chat';
    this.activeTemplate = 'runner';
    this.activeLang = 'html';
  }

  mount(selector) {
    this.root = document.querySelector(selector);
    document.documentElement.dataset.theme = this.settings.theme;
    document.documentElement.dataset.density = this.settings.density;
    this.renderShell();
    this.bind();
    this.renderAll();
  }

  renderShell() {
    this.root.innerHTML = `
      <div class="shell">
        <aside class="sidebar" id="sidebar" aria-label="Primary navigation">
          <div class="brand-row">
            <a class="brand" href="#" data-action="home"><span class="brand-mark bot-symbol" aria-hidden="true">🪐</span><span>wertx<span class="brand-dot">.</span>dev</span></a>
            <button class="icon-button mobile-close" data-action="close-sidebar" aria-label="Close menu">${icon('close')}</button>
          </div>
          <button class="new-chat" data-action="new-chat">${icon('plus')}<span>New conversation</span><kbd>⌘N</kbd></button>
          <button class="command-trigger" data-action="command">${icon('search')}<span>Search or jump to</span><kbd>⌘K</kbd></button>
          <div class="mode-switch" role="tablist" aria-label="AI mode">
            <button data-ai="wertx" role="tab"><span class="mode-symbol" aria-hidden="true">🪐</span><b>Wertx</b><small>Build</small></button>
            <button data-ai="point" role="tab"><span class="mode-symbol" aria-hidden="true">⚡</span><b>Point</b><small>Think</small></button>
          </div>
          <nav class="primary-nav">
            ${this.navItem('chat', 'Chat', 'chat')}
            ${this.navItem('play', 'Play area', 'play')}
            ${this.navItem('history', 'History', 'history')}
          </nav>
          <section class="sidebar-section model-section">
            <div class="section-label"><span>Models</span><span id="modelCount"></span></div>
            <div id="modelList" class="model-list"></div>
          </section>
          <section class="sidebar-section recent-section">
            <div class="section-label"><span>Recent</span><button data-view="history">View all</button></div>
            <div id="recentList" class="recent-list"></div>
          </section>
          <div class="sidebar-bottom">
            <button class="nav-link" data-view="settings">${icon('settings')}<span>Settings</span></button>
            <div class="local-status"><span></span><div><b>AI connected</b><small>Chats stored in this browser</small></div></div>
          </div>
        </aside>
        <div class="scrim" data-action="close-sidebar"></div>
        <header class="mobile-header">
          <button class="icon-button" data-action="open-sidebar" aria-label="Open menu">${icon('menu')}</button>
          <span class="brand"><span class="brand-mark bot-symbol" aria-hidden="true">🪐</span>wertx.dev</span>
          <button class="icon-button" data-action="new-chat" aria-label="New conversation">${icon('plus')}</button>
        </header>
        <main class="workspace">
          <section class="view chat-view" data-view-panel="chat">
            <header class="view-header">
              <div><div class="eyebrow" id="chatEyebrow">WERTX / BUILD MODE</div><h1 id="chatHeading">Start building</h1></div>
              <button class="model-pill" data-action="focus-model"><span class="status-dot"></span><span id="headerModel"></span><span class="chevron">⌄</span></button>
            </header>
            <div class="messages" id="messages"></div>
            <div class="composer-zone">
              <div class="file-preview" id="filePreview"></div>
              <div class="composer" id="composer">
                <textarea id="prompt" rows="1" maxlength="12000" aria-label="Message" placeholder="Ask Wertx to build, debug, or explain…"></textarea>
                <div class="composer-tools">
                  <div class="tool-left">
                    <button class="tool-button" data-action="upload" aria-label="Attach files">${icon('attach')}<span>Attach</span></button>
                    <input id="fileInput" type="file" multiple hidden accept=".js,.ts,.jsx,.tsx,.py,.html,.css,.json,.md,.txt,.csv,image/*">
                    <span class="context-label" id="contextLabel">0 / 12,000</span>
                  </div>
                  <button class="send-button" data-action="send" disabled aria-label="Send message">${icon('send')}</button>
                </div>
              </div>
              <p class="composer-note">Powered by Workers AI · attached files are sent only with your message</p>
            </div>
          </section>

          <section class="view play-view" data-view-panel="play" hidden>
            <header class="view-header play-topbar">
              <div><div class="eyebrow">PLAYGROUND / LIVE PREVIEW</div><h1>Build and run</h1></div>
              <div class="header-actions">
                <button class="quiet-button" data-action="clear-code">${icon('trash')} Clear</button>
                <button class="quiet-button" data-action="expand-preview">${icon('expand')} Preview</button>
                <button class="primary-button" data-action="run">${icon('play')} Run</button>
              </div>
            </header>
            <div class="template-rail" id="templateRail"></div>
            <div class="studio">
              <section class="editor-panel">
                <div class="editor-tabs" role="tablist">
                  <button data-lang="html" class="active">HTML</button><button data-lang="css">CSS</button><button data-lang="js">JavaScript</button>
                  <span class="editor-file" id="editorFile">index.html</span>
                </div>
                <textarea class="code-editor" id="editor" spellcheck="false" aria-label="Code editor"></textarea>
              </section>
              <section class="preview-panel" id="previewPanel">
                <div class="preview-bar"><span><i></i><i></i><i></i></span><b id="previewTitle">Preview</b><small id="previewStatus">Ready</small></div>
                <iframe id="previewFrame" title="Code preview" sandbox="allow-scripts allow-same-origin allow-pointer-lock" referrerpolicy="no-referrer"></iframe>
                <div class="preview-empty" id="previewEmpty">${icon('code', 28)}<b>Nothing running yet</b><span>Choose a template or write code, then press Run.</span></div>
                <div class="preview-error" id="previewError" hidden></div>
              </section>
            </div>
          </section>

          <section class="view list-view" data-view-panel="history" hidden>
            <header class="view-header"><div><div class="eyebrow">YOUR WORKSPACE</div><h1>Conversation history</h1></div><button class="danger-button" data-action="clear-history">Clear history</button></header>
            <div class="content-wrap"><div class="history-toolbar"><span id="historyCount">0 conversations</span><input id="historySearch" placeholder="Filter conversations…"></div><div class="history-grid" id="historyGrid"></div></div>
          </section>

          <section class="view list-view" data-view-panel="settings" hidden>
            <header class="view-header"><div><div class="eyebrow">PREFERENCES</div><h1>Settings</h1></div></header>
            <div class="settings-wrap">
              ${this.settingSection('Interface', `
                ${this.settingRow('Theme', 'Choose the workspace appearance', `<select id="themeSetting"><option value="dark">Dark</option><option value="light">Light</option></select>`)}
                ${this.settingRow('Density', 'Adjust spacing throughout the interface', `<select id="densitySetting"><option value="comfortable">Comfortable</option><option value="compact">Compact</option></select>`)}
              `)}
              ${this.settingSection('Play area', `
                ${this.settingRow('Auto-run templates', 'Run a template as soon as it is selected', this.toggle('autoRunSetting'))}
              `)}
              ${this.settingSection('Response', `
                ${this.settingRow('Response animation', 'Show activity while AI is working', this.toggle('streamSetting'))}
              `)}
              ${this.settingSection('Privacy', `
                ${this.settingRow('Browser storage', 'Chats, preferences, and tasks remain on this device', `<span class="secure-label">${icon('check')} Local only</span>`)}
                ${this.settingRow('Reset workspace', 'Delete all conversations and preferences', `<button class="danger-button" data-action="reset-data">Reset all data</button>`)}
              `)}
              <div class="about-strip"><span class="brand-mark bot-symbol" aria-hidden="true">🪐</span><div><b>wertx.dev</b><small>AI workspace · version 2.2</small></div></div>
            </div>
          </section>
        </main>
        <div class="command-palette" id="commandPalette" hidden>
          <button class="palette-backdrop" data-action="close-command" aria-label="Close command palette"></button>
          <div class="palette-dialog" role="dialog" aria-modal="true" aria-label="Command palette">
            <div class="palette-input">${icon('search')}<input id="commandInput" placeholder="Search views, models, and templates…"><kbd>ESC</kbd></div>
            <div class="palette-results" id="commandResults"></div>
          </div>
        </div>
        <div class="toast" id="toast" role="status"></div>
      </div>`;
  }

  navItem(view, label, iconName) { return `<button class="nav-link" data-view="${view}">${icon(iconName)}<span>${label}</span></button>`; }
  settingSection(title, content) { return `<section class="settings-card"><h2>${title}</h2>${content}</section>`; }
  settingRow(title, description, control) { return `<div class="setting-row"><div><b>${title}</b><span>${description}</span></div><div>${control}</div></div>`; }
  toggle(id) { return `<label class="switch"><input id="${id}" type="checkbox"><span></span></label>`; }

  bind() {
    this.root.addEventListener('click', e => this.handleClick(e));
    this.root.addEventListener('change', e => this.handleChange(e));
    const prompt = this.q('#prompt');
    prompt.addEventListener('input', () => this.updateComposer());
    prompt.addEventListener('keydown', e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.send(); } });
    this.q('#fileInput').addEventListener('change', e => this.addFiles(e.target.files));
    ['dragenter', 'dragover'].forEach(type => this.q('#composer').addEventListener(type, e => { e.preventDefault(); this.q('#composer').classList.add('dragging'); }));
    ['dragleave', 'drop'].forEach(type => this.q('#composer').addEventListener(type, e => { e.preventDefault(); this.q('#composer').classList.remove('dragging'); if (type === 'drop') this.addFiles(e.dataTransfer.files); }));
    this.q('#editor').addEventListener('input', e => { this.getTemplateState()[this.activeLang] = e.target.value; if (this.settings.autoRun) { clearTimeout(this.runTimer); this.runTimer = setTimeout(() => this.runCode(), 500); } });
    this.q('#historySearch').addEventListener('input', () => this.renderHistory());
    this.q('#commandInput').addEventListener('input', () => this.renderCommands());
    window.addEventListener('message', e => { if (e.data?.source === 'wertx-preview-error') this.showPreviewError(e.data.message); });
    document.addEventListener('keydown', e => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'k') { e.preventDefault(); this.openCommand(); }
      if (mod && e.key.toLowerCase() === 'n') { e.preventDefault(); this.newChat(); }
      if (mod && e.key === 'Enter' && this.view === 'play') { e.preventDefault(); this.runCode(); }
      if (e.key === 'Escape') { this.closeCommand(); this.q('#sidebar').classList.remove('open'); }
    });
  }

  handleClick(e) {
    const action = e.target.closest('[data-action]')?.dataset.action;
    const view = e.target.closest('[data-view]')?.dataset.view;
    const ai = e.target.closest('[data-ai]')?.dataset.ai;
    const model = e.target.closest('[data-model]')?.dataset.model;
    const lang = e.target.closest('[data-lang]')?.dataset.lang;
    const templateId = e.target.closest('[data-template]')?.dataset.template;
    const chatId = e.target.closest('[data-chat]')?.dataset.chat;
    const deleteId = e.target.closest('[data-delete-chat]')?.dataset.deleteChat;
    if (view) this.switchView(view);
    if (ai) this.switchAI(ai);
    if (model) this.selectModel(model);
    if (lang) this.switchLang(lang);
    if (templateId) this.loadTemplate(templateId);
    if (chatId) this.loadChat(chatId);
    if (deleteId) { e.stopPropagation(); this.deleteChat(deleteId); }
    if (!action) return;
    const actions = {
      home: () => this.switchView('chat'),
      'new-chat': () => this.newChat(),
      command: () => this.openCommand(),
      'close-command': () => this.closeCommand(),
      'open-sidebar': () => this.q('#sidebar').classList.add('open'),
      'close-sidebar': () => this.q('#sidebar').classList.remove('open'),
      upload: () => this.q('#fileInput').click(),
      send: () => this.send(),
      run: () => this.runCode(),
      'clear-code': () => this.clearCode(),
      'expand-preview': () => this.q('#previewPanel').classList.toggle('expanded'),
      'focus-model': () => { this.q('.model-section').scrollIntoView({ behavior: 'smooth' }); this.q('.model-section').classList.add('pulse'); setTimeout(() => this.q('.model-section').classList.remove('pulse'), 900); },
      'clear-history': () => this.clearHistory(),
      'reset-data': () => this.resetData()
    };
    actions[action]?.();
  }

  handleChange(e) {
    if (e.target.id === 'themeSetting') { this.settings.theme = e.target.value; document.documentElement.dataset.theme = e.target.value; this.saveSettings(); }
    if (e.target.id === 'densitySetting') { this.settings.density = e.target.value; document.documentElement.dataset.density = e.target.value; this.saveSettings(); }
    if (e.target.id === 'autoRunSetting') { this.settings.autoRun = e.target.checked; this.saveSettings(); }
    if (e.target.id === 'streamSetting') { this.settings.stream = e.target.checked; this.saveSettings(); }
  }

  renderAll() {
    this.renderModels();
    this.renderRecent();
    this.renderMessages();
    this.renderTemplates();
    this.renderHistory();
    this.syncSettings();
    this.updateChrome();
    this.loadTemplate(this.activeTemplate, false);
  }

  switchView(view) {
    this.view = view;
    this.qa('[data-view-panel]').forEach(panel => panel.hidden = panel.dataset.viewPanel !== view);
    this.qa('.nav-link[data-view]').forEach(button => button.classList.toggle('active', button.dataset.view === view));
    this.q('#sidebar').classList.remove('open');
    if (view === 'history') this.renderHistory();
    if (view === 'play') setTimeout(() => this.runCode(), 30);
  }

  switchAI(ai) {
    if (!MODELS[ai]) return;
    this.ai = ai;
    localStorage.setItem('wertx.ai', ai);
    this.newChat(false);
    this.renderModels();
    this.renderRecent();
    this.updateChrome();
  }

  selectModel(id) {
    if (!MODELS[this.ai].some(model => model.id === id)) return;
    this.models[this.ai] = id;
    localStorage.setItem('wertx.models', JSON.stringify(this.models));
    this.renderModels();
    this.updateChrome();
    this.toast(`Switched to ${this.currentModel().version} ${this.currentModel().name}`);
  }

  currentModel() { return MODELS[this.ai].find(m => m.id === this.models[this.ai]) || MODELS[this.ai][0]; }

  updateChrome() {
    this.qa('[data-ai]').forEach(button => { button.classList.toggle('active', button.dataset.ai === this.ai); button.setAttribute('aria-selected', String(button.dataset.ai === this.ai)); });
    const model = this.currentModel();
    this.q('#headerModel').textContent = `${model.version} ${model.name} · ${model.tier}`;
    this.q('#chatEyebrow').textContent = this.ai === 'wertx' ? 'WERTX / BUILD MODE' : 'POINT / THINK MODE';
    this.q('#chatHeading').textContent = this.messages.length ? (this.ai === 'wertx' ? 'Building with Wertx' : 'Thinking with Point') : (this.ai === 'wertx' ? 'What should we build?' : 'What are you working through?');
    this.q('#prompt').placeholder = this.ai === 'wertx' ? 'Ask Wertx to build, debug, or explain…' : 'Ask Point anything…';
  }

  renderModels() {
    const list = MODELS[this.ai];
    this.q('#modelCount').textContent = `${list.length} available`;
    this.q('#modelList').innerHTML = list.map(m => `<button class="model-row ${m.id === this.models[this.ai] ? 'active' : ''}" data-model="${m.id}"><span class="model-monogram">${m.name[0]}</span><span><b>${m.version} ${m.name}</b><small>${m.tier}</small></span>${m.id === this.models[this.ai] ? icon('check', 15) : ''}</button>`).join('');
  }

  renderRecent() {
    const recent = this.chats.filter(c => c.ai === this.ai).slice(0, 4);
    this.q('#recentList').innerHTML = recent.length ? recent.map(c => `<button data-chat="${c.id}"><span>${escapeHtml(c.title)}</span><small>${this.relativeTime(c.updated)}</small></button>`).join('') : '<p>No conversations yet</p>';
  }

  newChat(focus = true) {
    this.chatId = null;
    this.messages = [];
    this.files = [];
    this.renderMessages();
    this.renderFiles();
    this.switchView('chat');
    this.updateChrome();
    if (focus) this.q('#prompt').focus();
  }

  async send() {
    if (this.generating) {
      this.toast('Wertx is still generating the current response');
      return;
    }
    const input = this.q('#prompt');
    const text = input.value.trim();
    if (!text && !this.files.length) return;
    this.generating = true;
    const files = this.files.map(f => ({ name: f.name, size: f.size, type: f.type, content: f.content }));
    this.messages.push({ role: 'user', text: text || 'Review the attached files.', files, time: Date.now() });
    input.value = '';
    this.files = [];
    this.renderFiles();
    this.updateComposer();
    this.renderMessages(true);
    this.messages.push({ role: 'typing', time: Date.now() });
    this.renderMessages(true);
    const response = await this.generateResponse(text, files);
    this.messages = this.messages.filter(m => m.role !== 'typing');
    const installed = this.ai === 'wertx' ? this.installGeneratedGame(response, text) : false;
    const displayResponse = installed
      ? `Built and loaded **${this.templates.find(template => template.id === 'generated-game')?.name || 'your game'}** in Play area. It is running now — use the editor to change anything.`
      : response;
    this.messages.push({ role: 'assistant', text: displayResponse, time: Date.now() });
    this.renderMessages(true);
    this.saveChat();
    this.generating = false;
    this.updateComposer();
  }

  async generateResponse(text, files) {
    const apiMessages = this.messages
      .filter(message => message.role === 'user' || message.role === 'assistant')
      .slice(-16)
      .map(message => ({
        role: message.role,
        content: this.messageForAI(message)
      }));
    try {
      const response = await fetch(AI_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: this.ai, model: this.models[this.ai], messages: apiMessages })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.response) throw new Error(data.detail || data.error || `Request failed (${response.status})`);
      return data.response.trim();
    } catch (error) {
      console.error('Wertx AI request failed', error);
      this.toast('AI connection failed — retry in a moment');
      return this.fallbackResponse(text, files);
    }
  }

  messageForAI(message) {
    let content = message.text || '';
    if (message.files?.length) {
      const attachments = message.files.map(file => {
        const body = file.content ? String(file.content).slice(0, 10000) : '[Binary file — content unavailable]';
        return `\n\nAttached file: ${file.name}\n\`\`\`\n${body}\n\`\`\``;
      }).join('');
      content += attachments;
    }
    return content;
  }

  fallbackResponse(text, files) {
    const lower = text.trim().toLowerCase();
    if (this.ai === 'point' && /^(hi|hello|hey|yo|good (morning|afternoon|evening))[!. ]*$/.test(lower)) return 'Hello! How can I help you?';
    if (files.length) return `I couldn't reach the AI service to review ${files.map(file => `**${file.name}**`).join(', ')}. Please retry in a moment.`;
    return this.ai === 'point'
      ? 'I couldn’t reach Point just now. Please retry in a moment.'
      : 'I couldn’t reach Wertx just now. Please retry and I’ll build it directly in the Play area.';
  }

  installGeneratedGame(response, requestText) {
    const getBlock = lang => {
      const pattern = lang === 'js' ? '(?:javascript|js)' : lang;
      return response.match(new RegExp('```' + pattern + '\\s*\\n([\\s\\S]*?)```', 'i'))?.[1]?.trim() || '';
    };
    const html = getBlock('html');
    const css = getBlock('css');
    const js = getBlock('js');
    if (!html || !css || !js) return false;
    const title = requestText.replace(/^(please\s+)?(make|build|create|generate)\s+(me\s+)?/i, '').trim().slice(0, 42) || 'Generated Game';
    const generated = {
      id: 'generated-game',
      name: title.replace(/\b\w/g, char => char.toUpperCase()),
      category: /THREE\./.test(js) ? '3D · AI' : 'AI Game',
      description: 'Generated by Wertx',
      html,
      css,
      js,
      needsThree: /THREE\./.test(js)
    };
    const existing = this.templates.findIndex(template => template.id === generated.id);
    if (existing >= 0) this.templates[existing] = generated; else this.templates.unshift(generated);
    if (!this.templateStates) this.templateStates = {};
    this.templateStates[generated.id] = { html, css, js };
    this.activeTemplate = generated.id;
    this.renderTemplates();
    this.switchLang('html');
    this.q('#previewTitle').textContent = generated.name;
    this.runCode();
    this.switchView('play');
    return true;
  }

  renderMessages(scroll = false) {
    const target = this.q('#messages');
    if (!this.messages.length) {
      const suggestions = this.ai === 'wertx' ? ['Load Velocity Run', 'Open Orbit Lab', 'Debug a JavaScript error'] : ['Plan my next project', 'Help me make a decision', 'Draft a concise email'];
      target.innerHTML = `<div class="empty-state"><div class="empty-kicker"><span></span>${this.ai === 'wertx' ? 'BUILD WITH CONTEXT' : 'THINK WITH CLARITY'}</div><h2>${this.ai === 'wertx' ? 'From first prompt to\nrunning game.' : 'A clearer way through\neveryday work.'}</h2><p>${this.ai === 'wertx' ? 'Describe a game and Wertx will generate, load, and run it in the workspace.' : 'Ask about writing, plans, decisions, ideas, learning, or anything else.'}</p><div class="prompt-grid">${suggestions.map(s => `<button data-suggestion="${escapeHtml(s)}"><span>${s}</span><b>↗</b></button>`).join('')}</div></div>`;
      target.querySelectorAll('[data-suggestion]').forEach(button => button.onclick = () => { this.q('#prompt').value = button.dataset.suggestion; this.updateComposer(); this.q('#prompt').focus(); });
    } else {
      target.innerHTML = `<div class="message-thread">${this.messages.map(m => this.messageHTML(m)).join('')}</div>`;
      target.querySelectorAll('[data-copy-message]').forEach(button => button.onclick = async () => { const msg = this.messages[+button.dataset.copyMessage]; await navigator.clipboard.writeText(msg.text); this.toast('Copied to clipboard'); });
    }
    this.updateChrome();
    if (scroll) requestAnimationFrame(() => target.scrollTo({ top: target.scrollHeight, behavior: 'smooth' }));
  }

  messageHTML(message) {
    if (message.role === 'typing') return `<article class="message assistant"><div class="message-mark bot-symbol">${this.ai === 'wertx' ? '🪐' : '⚡'}</div><div class="message-card"><div class="typing typing-${this.ai}" aria-label="${this.ai === 'wertx' ? 'Wertx is building' : 'Point is thinking'}"><i></i><i></i><i></i></div></div></article>`;
    const index = this.messages.indexOf(message);
    const files = message.files?.length ? `<div class="message-files">${message.files.map(f => `<span>${icon('attach', 13)}${escapeHtml(f.name)}</span>`).join('')}</div>` : '';
    return `<article class="message ${message.role}"><div class="message-mark ${message.role === 'assistant' ? 'bot-symbol' : ''}">${message.role === 'user' ? 'a' : (this.ai === 'wertx' ? '🪐' : '⚡')}</div><div class="message-card"><header><b>${message.role === 'user' ? 'You' : (this.ai === 'wertx' ? 'Wertx' : 'Point')}</b><time>${new Date(message.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time></header>${files}<div class="message-text">${this.formatMarkdown(message.text)}</div>${message.role === 'assistant' ? `<footer><button data-copy-message="${index}">${icon('copy', 14)} Copy</button><button data-view="play">${icon('play', 14)} Open Play area</button></footer>` : ''}</div></article>`;
  }

  formatMarkdown(text = '') {
    let safe = escapeHtml(text);
    safe = safe.replace(/```(?:\w+)?\n([\s\S]*?)```/g, '<pre><code>$1</code></pre>');
    safe = safe.replace(/`([^`]+)`/g, '<code>$1</code>');
    safe = safe.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    return safe.replace(/\n/g, '<br>');
  }

  async addFiles(fileList) {
    const accepted = Array.from(fileList).slice(0, 8);
    for (const file of accepted) {
      const textLike = file.type.startsWith('text/') || /\.(js|ts|jsx|tsx|py|html|css|json|md|txt|csv)$/i.test(file.name);
      const content = textLike && file.size < 1_000_000 ? await file.text() : null;
      this.files.push({ name: file.name, size: file.size, type: file.type, content });
    }
    this.renderFiles();
    this.updateComposer();
  }

  renderFiles() {
    this.q('#filePreview').innerHTML = this.files.map((f, i) => `<span>${icon('attach', 13)}<b>${escapeHtml(f.name)}</b><small>${this.formatBytes(f.size)}</small><button data-remove-file="${i}" aria-label="Remove ${escapeHtml(f.name)}">×</button></span>`).join('');
    this.q('#filePreview').querySelectorAll('[data-remove-file]').forEach(button => button.onclick = () => { this.files.splice(+button.dataset.removeFile, 1); this.renderFiles(); this.updateComposer(); });
  }

  updateComposer() {
    const input = this.q('#prompt');
    input.style.height = '0px';
    input.style.height = `${Math.min(180, Math.max(28, input.scrollHeight))}px`;
    this.q('#contextLabel').textContent = `${input.value.length.toLocaleString()} / 12,000`;
    this.q('.send-button').disabled = this.generating || (!input.value.trim() && !this.files.length);
  }

  renderTemplates() {
    this.q('#templateRail').innerHTML = this.templates.map((t, i) => `<button class="template-chip ${t.id === this.activeTemplate ? 'active' : ''}" data-template="${t.id}"><span>${String(i + 1).padStart(2, '0')}</span><div><b>${t.name}</b><small>${t.category}</small></div></button>`).join('');
  }

  getTemplateState() {
    if (!this.templateStates) this.templateStates = {};
    if (!this.templateStates[this.activeTemplate]) {
      const t = this.templates.find(item => item.id === this.activeTemplate);
      this.templateStates[this.activeTemplate] = { html: t?.html || '', css: t?.css || '', js: t?.js || '' };
    }
    return this.templateStates[this.activeTemplate];
  }

  loadTemplate(id, run = true) {
    const template = this.templates.find(t => t.id === id);
    if (!template) return;
    this.activeTemplate = id;
    this.getTemplateState();
    this.renderTemplates();
    this.switchLang(this.activeLang);
    this.q('#previewTitle').textContent = template.name;
    if (run && this.settings.autoRun) this.runCode();
  }

  switchLang(lang) {
    this.activeLang = lang;
    this.qa('[data-lang]').forEach(button => button.classList.toggle('active', button.dataset.lang === lang));
    this.q('#editor').value = this.getTemplateState()[lang] || '';
    this.q('#editorFile').textContent = ({ html: 'index.html', css: 'styles.css', js: 'app.js' })[lang];
  }

  runCode() {
    const state = this.getTemplateState();
    const template = this.templates.find(t => t.id === this.activeTemplate);
    const frame = this.q('#previewFrame');
    this.q('#previewEmpty').hidden = true;
    this.q('#previewError').hidden = true;
    this.q('#previewStatus').textContent = 'Running';
    const safeJs = state.js.replace(/<\/script/gi, '<\\/script');
    const execute = template?.needsThree
      ? `import('/vendor/three.module.js').then(THREE=>{window.THREE=THREE;try{new Function(${JSON.stringify(safeJs)})()}catch(error){window.parent.postMessage({source:'wertx-preview-error',message:error.message},'*')}}).catch(error=>window.parent.postMessage({source:'wertx-preview-error',message:'Three.js failed to load: '+error.message},'*'));`
      : `try{new Function(${JSON.stringify(safeJs)})()}catch(error){window.parent.postMessage({source:'wertx-preview-error',message:error.message},'*')}`;
    frame.srcdoc = `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>${state.css}</style></head><body>${state.html}<script type="module">${execute}<\/script></body></html>`;
    frame.onload = () => { this.q('#previewStatus').textContent = 'Live'; };
  }

  showPreviewError(message) {
    const box = this.q('#previewError');
    box.hidden = false;
    box.innerHTML = `<b>Runtime error</b><span>${escapeHtml(message)}</span>`;
    this.q('#previewStatus').textContent = 'Error';
  }

  clearCode() {
    this.templateStates[this.activeTemplate] = { html: '', css: '', js: '' };
    this.switchLang(this.activeLang);
    this.q('#previewFrame').srcdoc = '';
    this.q('#previewEmpty').hidden = false;
    this.q('#previewStatus').textContent = 'Ready';
  }

  saveChat() {
    const first = this.messages.find(m => m.role === 'user');
    if (!first) return;
    if (!this.chatId) this.chatId = crypto.randomUUID?.() || `chat-${Date.now()}`;
    const chat = { id: this.chatId, ai: this.ai, model: this.models[this.ai], title: first.text.slice(0, 72) || first.files?.[0]?.name || 'Untitled conversation', updated: Date.now(), messages: this.messages.filter(m => m.role !== 'typing') };
    const index = this.chats.findIndex(c => c.id === chat.id);
    if (index >= 0) this.chats[index] = chat; else this.chats.unshift(chat);
    this.chats.sort((a, b) => b.updated - a.updated);
    this.chats = this.chats.slice(0, 80);
    localStorage.setItem('wertx.chats.v2', JSON.stringify(this.chats));
    this.renderRecent();
  }

  loadChat(id) {
    const chat = this.chats.find(c => c.id === id);
    if (!chat) return;
    this.chatId = chat.id;
    this.ai = chat.ai;
    if (MODELS[this.ai].some(m => m.id === chat.model)) this.models[this.ai] = chat.model;
    this.messages = chat.messages || [];
    this.renderModels();
    this.renderRecent();
    this.renderMessages(true);
    this.switchView('chat');
    this.updateChrome();
  }

  deleteChat(id) {
    this.chats = this.chats.filter(c => c.id !== id);
    localStorage.setItem('wertx.chats.v2', JSON.stringify(this.chats));
    if (this.chatId === id) this.newChat(false);
    this.renderHistory();
    this.renderRecent();
  }

  renderHistory() {
    const query = this.q('#historySearch')?.value.toLowerCase() || '';
    const filtered = this.chats.filter(c => !query || c.title.toLowerCase().includes(query));
    this.q('#historyCount').textContent = `${filtered.length} conversation${filtered.length === 1 ? '' : 's'}`;
    this.q('#historyGrid').innerHTML = filtered.length ? filtered.map(c => {
      const model = MODELS[c.ai]?.find(m => m.id === c.model);
      return `<article class="history-card" data-chat="${c.id}"><div class="history-ai">${c.ai === 'wertx' ? 'w' : 'p'}</div><div><h3>${escapeHtml(c.title)}</h3><p>${c.ai === 'wertx' ? 'Wertx' : 'Point'} · ${model ? `${model.version} ${model.name}` : 'Model'} · ${this.relativeTime(c.updated)}</p></div><button data-delete-chat="${c.id}" aria-label="Delete conversation">${icon('trash', 16)}</button></article>`;
    }).join('') : `<div class="empty-list">${icon('history', 24)}<h3>No conversations found</h3><p>Your saved chats will appear here.</p></div>`;
  }

  clearHistory() {
    if (!this.chats.length) return this.toast('History is already empty');
    if (confirm('Clear all saved conversations? This cannot be undone.')) {
      this.chats = [];
      localStorage.removeItem('wertx.chats.v2');
      this.newChat(false);
      this.renderHistory();
      this.renderRecent();
      this.toast('History cleared');
    }
  }

  syncSettings() {
    this.q('#themeSetting').value = this.settings.theme;
    this.q('#densitySetting').value = this.settings.density;
    this.q('#autoRunSetting').checked = this.settings.autoRun;
    this.q('#streamSetting').checked = this.settings.stream;
  }

  saveSettings() { localStorage.setItem('wertx.settings', JSON.stringify(this.settings)); }

  resetData() {
    if (confirm('Reset the entire local workspace? This removes chats and preferences.')) {
      ['wertx.ai', 'wertx.models', 'wertx.settings', 'wertx.chats.v2'].forEach(k => localStorage.removeItem(k));
      location.reload();
    }
  }

  openCommand() {
    this.q('#commandPalette').hidden = false;
    this.q('#commandInput').value = '';
    this.renderCommands();
    requestAnimationFrame(() => this.q('#commandInput').focus());
  }

  closeCommand() { this.q('#commandPalette').hidden = true; }

  renderCommands() {
    const query = this.q('#commandInput').value.toLowerCase();
    const items = [
      ...['chat', 'play', 'history', 'settings'].map(view => ({ type: 'View', title: view === 'play' ? 'Play area' : view[0].toUpperCase() + view.slice(1), meta: 'Navigate', run: () => this.switchView(view) })),
      ...MODELS[this.ai].map(m => ({ type: 'Model', title: `${m.version} ${m.name}`, meta: m.tier, run: () => this.selectModel(m.id) })),
      ...this.templates.map(t => ({ type: 'Template', title: t.name, meta: t.category, run: () => { this.loadTemplate(t.id); this.switchView('play'); } }))
    ].filter(item => !query || `${item.title} ${item.type} ${item.meta}`.toLowerCase().includes(query));
    this.commandItems = items;
    this.q('#commandResults').innerHTML = items.length ? items.map((item, i) => `<button data-command-index="${i}"><span class="command-type">${item.type[0]}</span><div><b>${item.title}</b><small>${item.type} · ${item.meta}</small></div><kbd>↵</kbd></button>`).join('') : '<p>No matching commands</p>';
    this.q('#commandResults').querySelectorAll('[data-command-index]').forEach(button => button.onclick = () => { items[+button.dataset.commandIndex].run(); this.closeCommand(); });
  }

  toast(message) {
    const toast = this.q('#toast');
    toast.textContent = message;
    toast.classList.add('show');
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => toast.classList.remove('show'), 2200);
  }

  relativeTime(timestamp) {
    const delta = Date.now() - timestamp;
    if (delta < 60_000) return 'now';
    if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
    if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
    return new Date(timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  formatBytes(bytes) { if (bytes < 1024) return `${bytes} B`; if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`; return `${(bytes / 1_048_576).toFixed(1)} MB`; }
  q(selector) { return this.root.querySelector(selector); }
  qa(selector) { return [...this.root.querySelectorAll(selector)]; }
}
